import {
  Button,
  IconButton,
  IconColor,
  IconName,
  Input,
  InputVariant,
  SnackbarVariant,
  Text,
  TextSize,
  TextWeight,
  makeStyles,
  useSnackbar
} from '@focaldata/cin-ui-components'
import Drawer from '@mui/material/Drawer'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { draftQuestionnaireRefetchQuery } from '../../../../data/gql/questionnaire/queries'
import { GET_AUDIENCES } from '../../../../data/gql/questionnaire/queries/audiences'
import { GET_CUSTOM_CRITERIA } from '../../../../data/gql/questionnaire/queries/customCriteria'
import { fieldworkRefetchQuery } from '../../../../data/gql/questionnaire/queries/fieldwork'
import { useUpdateFieldworkV2 } from '../../../../hooks/audience/createOrUpdateFieldworkV2'
import { useFieldwork } from '../../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../../hooks/useIsAudienceReadOnly'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useSurveyId } from '../../../../hooks/useSurveyId'
import { AudiencePresetPickerContainer } from '../../../../modules/Audience/AudiencePresetPicker'
import { LanguageAllocationsContainer } from '../../../../modules/Audience/LanguageAllocations'
import { PanelPickerControl } from '../../../../modules/Audience/PanelPicker'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { useProjectMarkets } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { PositiveIntegerNumberFormatCustom } from '../../../../utils/NumberFormatCustom'
import {
  PanelSupplier,
  convertAudienceType,
  multiMarketPanelOptions
} from '../../../../utils/audienceHelper'
import {
  AUDIENCE_LABEL_MAX_LENGTH,
  composeMarketName,
  isCountryCodedMarketName,
  splitMarketName
} from '../../../../utils/marketFlags'
import ChangeBaseMarketDialog from '../ChangeBaseMarketDialog'
import {
  useRemoveMarket,
  useRenameMarket
} from '../ProjectPage/ProjectPage.hooks'
import RemoveMarketDialog from '../RemoveMarketDialog'
import { getMasterSurvey } from '../markets.utils'
import { BaseMarketSetting } from './BaseMarketSetting'
import { useBaseMarketSetting } from './useBaseMarketSetting'

const DRAWER_WIDTH = 384

const useStyles = makeStyles()({
  // Full-height flex column so the destructive action can be pinned to the
  // bottom of the drawer (marginTop: auto on removeSection).
  panel: {
    width: DRAWER_WIDTH,
    padding: 24,
    boxSizing: 'border-box',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24
  },
  section: { marginBottom: 24 },
  removeSection: {
    marginTop: 'auto',
    paddingTop: 24,
    borderTop: '1px solid #F1F3F7'
  }
})

/**
 * Per-market settings for a multi-market project, opened from the Audiences
 * step. Holds the things that are configured once per market (rather than on
 * the shared questionnaire): the panel supplier, the audience (custom or one
 * of the preset audiences for the market's locale), the language allocations
 * (multi-language split) and the target completes. All are stored on the
 * market's fieldwork and written via `updateFieldworkV2`. Scoped to the market
 * whose `:surveyId` is in the route (same as the rest of the Audiences
 * editor).
 */
export const MarketSettingsDrawer = ({
  open,
  onClose
}: {
  open: boolean
  onClose: () => void
}) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const projectId = useProjectId()
  const { fieldwork } = useFieldwork()
  const { isReadOnly } = useIsAudienceReadOnly()
  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const { enqueueSnackbar } = useSnackbar()
  const baseMarket = useBaseMarketSetting()

  // Same removal path as the project page's "Remove" (mutation + cache update);
  // here it is reached from the drawer, so after it succeeds we leave the
  // now-deleted market's page for the project overview.
  const { removeMarket, loading: removingMarket } = useRemoveMarket(projectId)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  // Label the confirmation with this market's name — the same label the project
  // page and market tabs show (base market resolved, added markets parsed).
  const marketName = useProjectMarkets().find(
    (market) => market.surveyId === surveyId
  )?.label

  const handleConfirmRemove = async () => {
    try {
      await removeMarket(surveyId)
      navigate(`/project/${projectId}`)
    } catch (error) {
      // Logged in useRemoveMarket; surface it here so a failed removal isn't
      // silent, and stay on the page (don't navigate) so the user can retry.
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to remove market',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  // Derived from the audience — there is no panelSupplierCode to read back, and
  // convertAudienceType throws when the fieldwork hasn't loaded.
  const audienceType = fieldwork?.audience[0]?.__typename
  const panelSupplier = audienceType
    ? convertAudienceType(audienceType)
    : undefined
  // Required on every update; the constant would run PureSpectrum catalogue
  // validation against a market that has left it.
  const panelSupplierCode = panelSupplier ?? PanelSupplier.PURESPECTRUM

  // The Select keeps showing the old supplier until the write lands, so picking
  // the same option again re-fires the switch rather than reverting it. A ref,
  // not state: the guard has to hold within a render, not across one.
  const switchingPanel = useRef(false)

  const commitPanelSupplier = async (event: {
    target: { value: unknown }
  }): Promise<void> => {
    const nextPanel = multiMarketPanelOptions.find(
      ({ value }) => value === event.target.value
    )
    if (!nextPanel || nextPanel.value === panelSupplier) return
    if (switchingPanel.current) return

    const previousLocale = fieldwork?.locale
    switchingPanel.current = true
    try {
      const result = await updateFieldworkV2({
        variables: {
          input: {
            surveyId,
            panelSupplierCode: String(nextPanel.value),
            // The audience is only rebuilt when a country is present, and the fd
            // branch also needs a description. Omit either and the mutation
            // succeeds without switching — hence the result check below.
            countryCode: previousLocale?.country,
            countryLanguage: previousLocale?.language,
            audienceDescription: nextPanel.name
          }
        },
        refetchQueries: [
          fieldworkRefetchQuery(surveyId),
          draftQuestionnaireRefetchQuery(projectId, surveyId, waveId),
          // A switch can re-resolve the locale, which drops standard audience
          // members server-side.
          GET_AUDIENCES,
          GET_CUSTOM_CRITERIA
        ]
      })

      const appliedType =
        result.data?.updateFieldworkV2?.audience[0]?.__typename
      if (
        !appliedType ||
        convertAudienceType(appliedType) !== nextPanel.value
      ) {
        enqueueSnackbar(`This market can’t be switched to ${nextPanel.name}.`, {
          variant: SnackbarVariant.Error
        })
        return
      }

      // A split left on a custom-audience market keeps driving sample counts
      // with no editor to fix it. Cleared in a second write, never alongside the
      // switch: the backend applies the two independently, so a switch that
      // no-ops would still destroy it. `{}` is the tri-state clear.
      const hadLanguageSplit = (fieldwork?.languageAllocations?.length ?? 0) > 0
      if (nextPanel.value === PanelSupplier.CUSTOM && hadLanguageSplit) {
        // Caught here rather than by the outer handler: the switch has already
        // landed, so a failure now is not a failed switch and must not be
        // reported as one.
        const cleared = await updateFieldworkV2({
          variables: {
            input: {
              surveyId,
              panelSupplierCode: String(nextPanel.value),
              languageAllocations: {}
            }
          }
        }).catch(() => undefined)
        if (!cleared?.data) {
          enqueueSnackbar(
            'The market moved to its own audience, but its language split could not be cleared. Switch back to PureSpectrum to edit it.',
            { variant: SnackbarVariant.Warning }
          )
        }
      }

      const nextLocale = result.data.updateFieldworkV2.locale
      if (
        previousLocale &&
        (nextLocale.country !== previousLocale.country ||
          nextLocale.language !== previousLocale.language)
      ) {
        enqueueSnackbar(
          'The market’s locale changed with the panel — check its targeting and screening.',
          { variant: SnackbarVariant.Warning }
        )
      }
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to update the panel',
        { variant: SnackbarVariant.Error }
      )
    } finally {
      switchingPanel.current = false
    }
  }

  // The target the market was created with lives in `samplesNeeded.unadjusted`
  // (see useMarketsCompletes). Keep a local draft so the field stays editable
  // while typing, re-syncing whenever the persisted value changes.
  const currentCompletes =
    fieldwork?.samplesNeeded.unadjusted ??
    fieldwork?.samplesNeeded.adjusted ??
    0
  const [completesDraft, setCompletesDraft] = useState<number | ''>(
    currentCompletes
  )
  useEffect(() => {
    setCompletesDraft(currentCompletes)
  }, [currentCompletes, open])

  const commitCompletes = async () => {
    if (completesDraft === '' || completesDraft === currentCompletes) return
    try {
      await updateFieldworkV2({
        variables: {
          input: {
            surveyId,
            panelSupplierCode,
            samplesNeeded: Number(completesDraft)
          }
        },
        refetchQueries: [
          fieldworkRefetchQuery(surveyId),
          draftQuestionnaireRefetchQuery(projectId, surveyId, waveId)
        ]
      })
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : 'Failed to update target completes',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  const handleCompletesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setCompletesDraft(value ? Number(value) : '')
  }

  // The audience label is the middle segment of the market's survey name
  // ("Country — Label (XX)") — its only store. Cache-first read of the same
  // PROJECT_BY_ID query useProjectMarkets already issues here.
  const { project } = useGetProjectByProjectId()
  const { renameMarket, loading: renamingMarket } = useRenameMarket(projectId)
  const survey = project?.surveys.find(
    (projectSurvey) => projectSurvey.surveyId === surveyId
  )
  // A master created before masters were named as markets carries the project
  // name (no country prefix, no code) and every surface labels it from its
  // locale instead, so a label written here would be invisible.
  const hideAudienceLabel =
    getMasterSurvey(project)?.surveyId === surveyId &&
    !isCountryCodedMarketName(survey?.name)

  const nameParts = splitMarketName(survey?.name ?? '')
  const persistedLabel = nameParts.audienceLabel
  const [labelDraft, setLabelDraft] = useState(persistedLabel)
  // Also re-seeded on open/close: the drawer stays mounted when dismissed, so
  // an abandoned edit would otherwise linger and be committed by a later blur.
  useEffect(() => {
    setLabelDraft(persistedLabel)
  }, [persistedLabel, open])

  const commitLabel = async () => {
    if (!survey) return
    const nextName = composeMarketName({
      ...nameParts,
      audienceLabel: labelDraft
    })
    // Reset on a no-op (e.g. trailing spaces) so the field can never keep
    // showing text that was not persisted.
    if (labelDraft === persistedLabel || nextName === survey.name) {
      setLabelDraft(persistedLabel)
      return
    }
    try {
      await renameMarket(surveyId, nextName)
    } catch (error) {
      setLabelDraft(persistedLabel)
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : 'Failed to update audience label',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ style: { width: DRAWER_WIDTH } }}
      >
        <div className={classes.panel}>
          <div className={classes.header}>
            <Text size={TextSize.xl} weight={TextWeight.Bold}>
              Market settings
            </Text>
            <IconButton
              ariaLabel="Close settings"
              iconName={IconName.Cancel}
              iconColor={IconColor.Background}
              onClick={onClose}
            />
          </div>

          {survey && !hideAudienceLabel && (
            <div className={classes.section}>
              <Text size={TextSize.m} weight={TextWeight.SemiBold}>
                Audience label
              </Text>
              <Input
                ariaLabel="Audience label"
                fullWidth
                variant={InputVariant.Filled}
                placeholder="e.g. Scotland Boost"
                value={labelDraft}
                maxLength={AUDIENCE_LABEL_MAX_LENGTH}
                onChange={(event) => setLabelDraft(event.target.value)}
                onBlur={commitLabel}
                // Editable even for a launched market: the label is only a
                // display name, so it stays changeable after fieldwork starts.
                disabled={renamingMarket}
              />
            </div>
          )}

          {baseMarket.enabled && (
            <div className={classes.section}>
              <BaseMarketSetting
                isBase={baseMarket.isBase}
                disabled={baseMarket.unknown || baseMarket.saving || isReadOnly}
                onChoose={baseMarket.choose}
              />
            </div>
          )}

          {/* The single-survey control — it disables itself for a launched
              market. */}
          {panelSupplier && (
            <div className={classes.section}>
              <PanelPickerControl
                panelCode={panelSupplier}
                panels={multiMarketPanelOptions}
                onChangeSelectPanel={commitPanelSupplier}
              />
            </div>
          )}

          {/* The same preset picker the single-survey audience page uses: it
              lists the presets for this market's locale, confirms the switch,
              and writes the preset through updateFieldworkV2 keyed by the
              route's surveyId. Choosing Custom closes the drawer so the
              criteria editor behind it is revealed for editing (the
              single-survey page opens its criteria dialog at that point). */}
          <div className={classes.section}>
            <AudiencePresetPickerContainer onChoseCustomPreset={onClose} />
          </div>

          <div className={classes.section}>
            <Text size={TextSize.m} weight={TextWeight.SemiBold}>
              Target completes
            </Text>
            <Input
              fullWidth
              variant={InputVariant.Filled}
              value={completesDraft}
              onChange={handleCompletesChange}
              onBlur={commitCompletes}
              customFormat={PositiveIntegerNumberFormatCustom}
              disabled={isReadOnly}
            />
          </div>

          {/* PureSpectrum-only (same gate as Audience.tsx): the language list
              and every allocation write go through the PureSpectrum catalogue,
              which rejects a market that isn't on it. */}
          {panelSupplier === PanelSupplier.PURESPECTRUM && (
            <div className={classes.section}>
              <LanguageAllocationsContainer />
            </div>
          )}

          {/* Removing a market is only offered for draft markets. The drawer
              itself opens for launched ones too (for the audience label), so
              this guard is load-bearing. */}
          {!isReadOnly && (
            <div className={classes.removeSection}>
              <Button
                fullWidth
                onClick={() => setConfirmRemoveOpen(true)}
                disabled={removingMarket}
              >
                Remove this market
              </Button>
            </div>
          )}
        </div>
      </Drawer>

      {/* Rendered as siblings of the Drawer (not nested inside it) so the
          Dialogs' focus traps don't fight the Drawer's. */}
      <RemoveMarketDialog
        open={confirmRemoveOpen}
        marketName={marketName}
        busy={removingMarket}
        onConfirm={handleConfirmRemove}
        onClose={() => setConfirmRemoveOpen(false)}
      />
      <ChangeBaseMarketDialog
        open={baseMarket.confirming}
        marketName={marketName}
        busy={baseMarket.saving}
        onConfirm={baseMarket.confirm}
        onClose={baseMarket.cancel}
      />
    </>
  )
}
